<template>
  <treeselect
    :options="options"
    :placeholder="placeholder"
    :multiple="multiple"
    :clearable="clearable"
    :close-on-select="!multiple"
    :max-height="maxHeight"
    :append-to-body="appendToBody"
    :always-open="alwaysOpen"
    :limit="limitDisplay"
    :limit-text="getLimitDisplayText"
    v-model="selected"
    :disabled="busy || disabled"
    :before-clear-all="onBeforeClearAll"
    :defaultExpandLevel="10000"
    :valueFormat="returnObject ? 'object' : 'id'"
    :title="title"
    :z-index="zIndex"
    @input="onUpdate"
    @close="onClose"
  >
    <div v-if="showSelectAll && multiple" slot="before-list">
      <b-row class="border-bottom">
        <b-col class="font-italic">
          <a class="ml-2" href="#" @click.prevent="onTreeSelectAll()">
            <small>{{ trans('select_all') }}</small>
          </a>
        </b-col>
        <b-col class="text-right font-italic">
          <a class="mr-2" href="#" @click.prevent="onTreeClearAll()">
            <small>{{ trans('unselect_all') }}</small>
          </a>
        </b-col>
      </b-row>
    </div>
    <label slot="option-label" slot-scope="{node, labelClassName}" :class="labelClassName">
        <b-row>
            <b-col @click.self.stop.prevent>
                <span :title="compact([node.label, node.raw.subLabel]).join(', ')">{{ node.label }}</span>
                <small v-if="node.raw.subLabel" class="text-muted ml-2">{{ node.raw.subLabel }}</small>
                <tags
                  v-if="showTags && !!node.raw.tags"
                  :organization="organization"
                  :model="node.raw"
                  hide-edit
                  inline
                ></tags>
            </b-col>
            <b-col class="col-auto" v-if="showAdd">
                <b-button variant="link" size="sm" class="py-0" v-if="showChildAdd">
                    <i class="fa fa-fw fa-plus" @mousedown.stop.prevent @click="$emit('addChild', node)"></i>
                </b-button>
                <b-button variant="link" size="sm" class="py-0" >
                    <i class="fa fa-fw fa-edit" @mousedown.stop.prevent @click="$emit('modify', node)"></i>
                </b-button>
                <b-button variant="link" size="sm" class="py-0 text-danger" >
                    <i class="fa fa-fw fa-trash" @mousedown.stop.prevent @click="$emit('delete', node)"></i>
                </b-button>
            </b-col>
        </b-row>
    </label>
    <span slot="value-label" slot-scope="{ node }" :title="compact([node.label, node.raw.subLabel]).join(', ')">
      {{ node.label }}
    </span>
    <div v-if="showAdd " slot="after-list" class="text-right">
        <b-button variant="link" class="pl-0" @click="$emit('order')" v-if="showReorder">
        <small>
            <i class="fa fa-fw fa-bars"></i>
            {{ trans('reorder') }}
        </small>
        </b-button>
        <b-button variant="link" class="pl-0" @click="$emit('add')" >
        <small>
            <i class="fa fa-fw fa-plus"></i>
            {{ addText }}
        </small>
        </b-button>
    </div>
  </treeselect>
</template>

<script>
  import Treeselect from '@riophae/vue-treeselect'
  import Tags from './../../frontend/components/tag/Tags'

  function getModelLabel(model, attr) {
    if (typeof attr == 'function') return attr(model)
    return model[attr]
  }

  export default {
    props: {
      organization: Object,
      value: [Array, Number, String, Object],
      filter: Array,
      models: Array,
      multiple: {
        type: Boolean,
        default: true,
      },
      clearable: {
        type: Boolean,
        default: true,
      },
      url: String,
      placeholder: {
        type: String,
        default: trans('item'),
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      idAttribute: {
        type: String,
        default: 'id',
      },
      labelAttribute: {
        type: String|Function,
        default: 'name',
      },
      subLabelAttribute: {
        type: String|Function,
        default: null,
      },
      showTags: {
        type: Boolean,
        default: false,
      },
      showSelectAll: {
        type: Boolean,
        default: false,
      },
      showAdd: {
        type: Boolean,
        default: false,
      },
      showReorder: {
        type: Boolean,
        default: true,
      },
      showChildAdd: {
        type: Boolean,
        default: false,
      },
      addText: {
        type: String,
        default: trans('add_item')
      },
      returnObject: {
        type: Boolean,
        default: false,
      },
      maxHeight: Number,
      appendToBody: {
        type: Boolean,
        default: false,
      },
      alwaysOpen: {
        type: Boolean,
        default: false,
      },
      zIndex: {
        type: Number,
        default: 999,
      },
      limitSelected: {
        type: Number,
        default: Infinity,
      },
      limitDisplay: {
        type: Number,
        default: Infinity,
      },
    },
    components: {
      Treeselect,
      Tags,
    },
    data() {
      return {
        trans: trans,
        compact: _.compact,
        busy: false,
        error: null,
        items: this.models || null,
        selected: this.value ? _.clone(this.value) : (this.multiple ? [] : null),
      }
    },
    computed: {
      selectedOption() {
        if (!this.selected || _.isArray(this.selected)) return null
        return _.find(this.items || null, {id: this.selected})
      },
      title() {
        let model = this.selectedOption
        if (!model) return null
        return _.compact([
          getModelLabel(model, this.labelAttribute),
          this.subLabelAttribute ? getModelLabel(model, this.subLabelAttribute) : null
        ]).join(', ')
      },
      options() {
        return _.map(this.items || (this.multiple ? this.selected : _.compact([this.selected])) || [], model => {
          // check if we have filter array
          if (this.filter && this.filter.length > 0) {
            if (!this.filter.includes(model[this.idAttribute])) return false
          }
          if (!model || !model[this.idAttribute] || !this.labelAttribute) return {id: model, label: '...'}
          let label = getModelLabel(model, this.labelAttribute)
          return {
            id: model[this.idAttribute],
            label: label,
            subLabel: this.subLabelAttribute ? getModelLabel(model, this.subLabelAttribute) : null,
            tags: getModelLabel(model, 'tags'),
            children: model.children ? model.children : 0
          }
        })
      },
    },
    watch: {
      value() {
        this.selected = this.value ? _.clone(this.value) : (this.multiple ? [] : null)
      },
      models() {
        this.items = this.models
      },
      busy() {
        this.$emit('busy', this.busy)
      },
    },
    methods: {
      getLimitDisplayText(count) {
        return trans('and_count_more' + (count > 1 ? '_many' : '')).replace(':count', count)
      },
      onTreeSelectAll(index) {
        this.selected = _.map(this.items, 'id')
        this.onUpdate()
      },
      onTreeClearAll(index) {
        this.selected = []
        this.onUpdate()
      },
      onUpdate() {
        if (this.multiple && this.selected.length > this.limitSelected) {
          this.selected.length = this.limitSelected
        }
        if (!_.isEqual(this.selected, this.value) && !this.multiple) {
          let objects = this.getSelectedObjs()
          this.$emit('input', this.returnObject ? (objects[0] || null) : (this.selected || null))
        }
      },
      onBeforeClearAll() {
        this.$nextTick(() => {
          this.$emit('input', this.multiple ? [] : null)
          this.$emit('change', this.multiple ? [] : null)
        })
        return true
      },
      onClose() {
        if (!_.isEqual(this.selected, this.value) && this.multiple) {
          let objects = this.getSelectedObjs()
          this.$emit('input', this.returnObject ? objects : this.selected)
          this.$emit('change', this.returnObject ? objects : this.selected)
        }
        this.$emit('close')
      },
      getSelectedObjs() {
        return _.filter(this.items || [], item => {
          if (!this.multiple && !!this.selected) {
            return this.selected[this.idAttribute] == item[this.idAttribute]
          }
          else if (!this.multiple && !this.selected) {
            return false
          }
          return _.map(this.selected, this.idAttribute).includes(item[this.idAttribute])
        })
      },
      load() {
        this.busy = true
        this.error = null
        return axios.get(this.url)
        .then(response => {
          this.busy = false
          this.items = response.data.data ? response.data.data : response.data
          this.$emit('loaded', this.items)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.modelSelect = this
      if (!this.models && this.url) this.load()
    },
  }
</script>
